import fetch from 'cross-fetch'
import { v4 as uuidv4 } from 'uuid'
import httpLogger from './http-logger'
import { Dispatch } from 'redux'

interface ApiParams {
  endpoint: string
  customEndpoint?: boolean
  path?: string
  method?: string
  obj?: object
  showLoading?: boolean
  secretKey?: string
  signal?: AbortSignal
}

interface LogParams {
  url: string
  method: string
  status: number
  body?: object
  response?: Response
}

class ApiAsync {
  private config: Record<string, string> = {}
  private dispatch: Dispatch | null = null
  private authToken = ''
  private logging = false
  private lang = 'en-US'

  setConfig(config: Record<string, string>) {
    this.config = config
  }

  setDispatch(dispatch: Dispatch) {
    this.dispatch = dispatch
  }

  setAuthToken(authToken: string) {
    this.authToken = authToken
  }

  enableLogging() {
    this.logging = true
  }

  disableLogging() {
    this.logging = false
  }

  async logSuccess({ url, method, status, body, response }: LogParams) {
    if (this.logging) {
      let result: object | undefined = undefined
      try {
        result = await response?.clone().json()
      } catch {
        // do nothing
      }
      httpLogger.logRequest({ url, method, status, body, result })
    }
  }

  async fetchWithToken(params: ApiParams, requestUuid: string) {
    let urlApiMS = ''

    if (params.customEndpoint) {
      urlApiMS = params.endpoint
    } else {
      urlApiMS = this.config[params.endpoint]
    }

    if (params.path) {
      urlApiMS = urlApiMS + params.path
    }

    if (undefined === params.showLoading) {
      params.showLoading = true
    }

    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'Accept-Language': this.getAcceptLanguage()
      // 'Accept-Encoding': 'gzip, compress, br'
    }

    if (this.authToken) {
      headers.AUTH_TOKEN = this.authToken
    }

    // temporário para utilizar um novo serviço de mock api
    if (params.secretKey) {
      headers['secret-key'] = params.secretKey
    }

    if (params.showLoading && this.dispatch) {
      this.dispatch({
        type: 'api/REQUESTED',
        data: {
          requestUuid
        }
      })
    }

    try {
      const result = await fetch(urlApiMS, {
        method: params.method || 'GET',
        headers,
        body: JSON.stringify(params.obj),
        signal: params.signal
      })
      this.logSuccess({
        url: urlApiMS,
        method: params.method || 'GET',
        status: result.status,
        body: params.obj,
        response: result
      })
      return result
    } finally {
      if (params.showLoading && this.dispatch) {
        this.dispatch({
          type: 'api/RECEIVED',
          data: {
            requestUuid
          }
        })
      }
    }
  }

  async requestSync(params: ApiParams) {
    const requestUuid = uuidv4()

    const response = await this.fetchWithToken(params, requestUuid)

    if (response.status === 409) {
      return response
    }
    return await response.json()
  }

  setAcceptLanguage(lang: string) {
    this.lang = lang
  }

  getAcceptLanguage() {
    switch (this.lang) {
      case 'pt-BR':
        return 'pt-BR, en-US'
      case 'de-DE':
        return 'de-DE, en-US'
      case 'es-419':
        return 'es-419, en-US'
      default:
        return 'en-US'
    }
  }
}

export default new ApiAsync()
